import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUp } from '../auth/cognito';
import {
  ApiError,
  composeAddress,
  listPublicProjectAddresses,
  resolveProject,
  type PublicAddress,
  type PublicProject,
} from '../api/client';
import { config } from '../config';

type Step = 'credentials' | 'address';

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('credentials');

  // Step 1 — credentials + project.
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [projectReferenceNumber, setProjectReferenceNumber] = useState('');
  const [consent, setConsent] = useState(false);
  const [project, setProject] = useState<PublicProject | null>(null);
  const [resolving, setResolving] = useState(false);
  const [credError, setCredError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Step 2 — address picker.
  const [addresses, setAddresses] = useState<PublicAddress[]>([]);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [addressError, setAddressError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (step !== 'address' || !project) return;
    setLoadingAddresses(true);
    setAddressError(null);
    listPublicProjectAddresses(project.pid)
      .then((r) => setAddresses(r.addresses))
      .catch(() => setAddressError('Failed to load addresses for this project.'))
      .finally(() => setLoadingAddresses(false));
  }, [step, project]);

  async function onResolveProject(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setCredError(null);
    setEmailError(null);
    if (password !== passwordConfirm) {
      setCredError('Passwords do not match.');
      return;
    }
    if (!projectReferenceNumber.trim()) {
      setCredError('Please enter the project reference number we emailed you.');
      return;
    }
    setResolving(true);
    try {
      const p = await resolveProject(projectReferenceNumber.trim());
      setProject(p);
      setSelectedAddressId('');
      setStep('address');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setCredError(
          `We couldn't find a project with that reference number. Please double-check the email we sent you, or contact support at ${config.supportEmail}.`,
        );
      } else {
        setCredError(err instanceof Error ? err.message : 'Could not verify the project.');
      }
    } finally {
      setResolving(false);
    }
  }

  async function onSubmitAddress(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setAddressError(null);
    const selected = addresses.find((a) => a.addressId === selectedAddressId);
    if (!selected) {
      setAddressError('Please select your address.');
      return;
    }
    setSubmitting(true);
    try {
      const emailLc = email.trim().toLowerCase();
      await signUp({
        email: emailLc,
        password,
        addressId: selected.addressId,
      });
      // Cognito does not persist pre-signup clientMetadata into post-confirmation.
      // Stash the addressId so VerifyEmailPage can replay it on confirmSignUp.
      try {
        localStorage.setItem(`signup:addressId:${emailLc}`, selected.addressId);
      } catch {
        /* storage unavailable — post-confirm will flag invalid_address_id, admin can recover */
      }
      navigate(`/verify-email?email=${encodeURIComponent(emailLc)}`);
    } catch (err) {
      const name = (err as { name?: string }).name;
      if (name === 'UsernameExistsException') {
        setEmailError(
          'An account with this email already exists. Log in or reset your password.',
        );
        setStep('credentials');
      } else {
        setAddressError(err instanceof Error ? err.message : 'Signup failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmitCred =
    email.trim().length > 0 &&
    password.length >= 10 &&
    passwordConfirm.length >= 10 &&
    projectReferenceNumber.trim().length > 0 &&
    consent &&
    !resolving;

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-black">
      <div className="w-full max-w-lg card">
        <h1 className="text-2xl font-semibold text-white">Create your account</h1>
        <p className="text-sm text-gray-400 mt-1">
          {step === 'credentials'
            ? 'Enter your details and the project reference number we emailed you.'
            : 'Pick your address from the list for this project.'}
        </p>

        {step === 'credentials' && (
          <form onSubmit={onResolveProject} className="mt-6 space-y-4">
            <div>
              <label className="label" htmlFor="signup-email">Email</label>
              <input
                id="signup-email"
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              {emailError && (
                <div className="text-sm text-red-400 mt-1">
                  {emailError}{' '}
                  <Link to="/login" className="underline hover:text-red-300">
                    Log in
                  </Link>{' '}
                  or{' '}
                  <Link to="/forgot-password" className="underline hover:text-red-300">
                    reset your password
                  </Link>
                  .
                </div>
              )}
            </div>

            <div>
              <label className="label" htmlFor="signup-password">Password</label>
              <input
                id="signup-password"
                className="input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
              <div className="text-xs text-gray-500 mt-1">
                At least 10 characters with upper- and lower-case letters and a digit.
              </div>
            </div>

            <div>
              <label className="label" htmlFor="signup-confirm-password">Confirm password</label>
              <input
                id="signup-confirm-password"
                className="input"
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                autoComplete="new-password"
                minLength={10}
                required
              />
            </div>

            <div>
              <label className="label" htmlFor="signup-project-ref">Project reference number</label>
              <input
                id="signup-project-ref"
                className="input"
                type="text"
                value={projectReferenceNumber}
                onChange={(e) => setProjectReferenceNumber(e.target.value.toUpperCase())}
                autoComplete="off"
                required
              />
            </div>

            <label className="flex items-start gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                className="mt-1 accent-red-600"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                required
              />
              <span>
                I agree to the{' '}
                <Link to="/privacy" className="text-red-400 hover:text-red-300 hover:underline" target="_blank">
                  Privacy Policy
                </Link>{' '}
                and acknowledge this is a{' '}
                <Link
                  to="/beta-disclaimer"
                  className="text-red-400 hover:text-red-300 hover:underline"
                  target="_blank"
                >
                  beta system
                </Link>
                .
              </span>
            </label>

            {credError && <div className="text-sm text-red-400">{credError}</div>}

            <button type="submit" className="btn-primary w-full" disabled={!canSubmitCred}>
              {resolving ? 'Verifying project…' : 'Continue'}
            </button>

            <div className="pt-2 border-t border-gray-800 text-sm text-gray-500">
              Already have an account?{' '}
              <Link to="/login" className="text-red-400 hover:text-red-300 hover:underline">
                Sign in
              </Link>
            </div>
          </form>
        )}

        {step === 'address' && project && (
          <form onSubmit={onSubmitAddress} className="mt-6 space-y-4">
            <div className="bg-gray-800 border border-gray-700 rounded-md p-3 text-sm">
              <div className="font-medium text-white">{project.displayName}</div>
              <div className="text-gray-300">{project.mainAddress}</div>
              <div className="text-xs text-gray-500 mt-1">
                Project reference number: {project.projectReferenceNumber}
              </div>
            </div>

            <fieldset>
              <legend className="label">Your address</legend>
              {loadingAddresses ? (
                <div className="text-sm text-gray-400">Loading addresses…</div>
              ) : addresses.length === 0 ? (
                <div className="text-sm text-gray-400">
                  No addresses have been set up for this project yet. Please contact support at{' '}
                  <a
                    className="text-red-400 hover:text-red-300 hover:underline"
                    href={`mailto:${config.supportEmail}`}
                  >
                    {config.supportEmail}
                  </a>
                  .
                </div>
              ) : (
                // Radio list (not <select size>) — list-box clicks don't
                // reliably commit selection on mobile Safari, leaving the
                // Create-account button stuck disabled. Each row is a
                // <label> wrapping a radio so clicking anywhere selects.
                <div
                  className="max-h-72 overflow-y-auto border border-gray-700 rounded-md divide-y divide-gray-800 bg-gray-900"
                  role="radiogroup"
                  aria-label="Your address"
                >
                  {addresses.map((a) => {
                    const checked = selectedAddressId === a.addressId;
                    return (
                      <label
                        key={a.addressId}
                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm ${
                          checked
                            ? 'bg-red-900/20 text-white'
                            : 'text-gray-200 hover:bg-gray-800/60'
                        }`}
                      >
                        <input
                          type="radio"
                          name="signup-address"
                          value={a.addressId}
                          checked={checked}
                          onChange={(e) => setSelectedAddressId(e.target.value)}
                          className="accent-red-600"
                          required
                        />
                        <span>{composeAddress(a)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </fieldset>

            {addressError && <div className="text-sm text-red-400">{addressError}</div>}

            <div className="flex gap-2">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setStep('credentials')}
                disabled={submitting}
              >
                Back
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={!selectedAddressId || submitting}
              >
                {submitting ? 'Creating account…' : 'Create account'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
